import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { ACCENT_GOLD } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import { OMENS, omenText, rewardLabel, canRerollOmens } from '../../utils/arena/oracle';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { t } from '../../localization/t';
import TarotCard from './TarotCard';
import ForgedButton from '../ui/ForgedButton';
import ForesightGauge from './ForesightGauge';

const ACCENT = getClass('oracle').accent;
// The board stays in view; only a soft fall-off behind the plates.
const BACKDROP = 'blur(6px) brightness(0.85)';

// The omen half of the Oracle's reading, at setup. Three tarot plates
// float over the blurred board; keep one and seal it. Props carry the
// server view and the host's actions; this screen only asks.
export default class OracleOverlay extends Component {
  state = { omenId: null };

  pick = (id) => {
    if (this.props.busy) return;
    playUI(UI.CLICK, { volume: 0.6 });
    this.setState((s) => ({ omenId: s.omenId === id ? null : id }));
  };

  reroll = () => {
    this.setState({ omenId: null });
    this.props.onReroll();
  };

  render() {
    const { reading, skillState, busy, error, onSeal } = this.props;
    const { omenId } = this.state;
    const omens = reading?.pending?.omens || [];
    const rerollCost = reading?.rerollCost || 1;
    const rerollable = canRerollOmens(skillState);
    const canReroll = rerollable && !omenId && (reading?.foresight || 0) >= rerollCost;
    const float = (delay) => ({
      initial: { opacity: 0, y: '1.2cqw' },
      animate: { opacity: 1, y: 0 },
      transition: { type: 'spring', duration: 0.45, bounce: 0, delay },
    });

    return (
      <motion.div
        className="fixed inset-0 z-[1150] flex flex-col items-center justify-center"
        style={{
          gap: '1.5cqw',
          background: 'radial-gradient(ellipse 60% 55% at 50% 50%, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.12) 100%)',
          backdropFilter: BACKDROP,
          WebkitBackdropFilter: BACKDROP,
        }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
        data-testid="oracle-overlay"
      >
        <motion.div
          className="arena-heading font-bold"
          style={{ color: ACCENT, fontSize: '2.5cqw', lineHeight: 1.05, textShadow: `0 0.125cqw 0.5cqw rgba(0,0,0,0.85), 0 0 1.5cqw ${ACCENT}55` }}
          {...float(0)}
        >
          {t('Keep an omen')}
        </motion.div>

        {/* Stretched so the three cards share one foot whatever their text runs to. */}
        <div className="flex items-stretch justify-center" style={{ gap: '2cqw', minHeight: '32cqw' }}>
          <AnimatePresence mode="popLayout">
            {omens.map((omen, i) => {
              const def = OMENS[omen.id];
              if (!def) return null;
              return (
                <motion.div
                  key={`${omen.id}-${i}`}
                  className="relative flex"
                  layout
                  initial={{ opacity: 0, y: '3cqw', rotate: (i - 1) * 5, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, y: '-1.5cqw', scale: 0.96 }}
                  transition={{ type: 'spring', duration: 0.5, bounce: 0, delay: 0.08 + i * 0.1 }}
                >
                  <TarotCard
                    art={def.art}
                    name={t(def.title)}
                    width={15}
                    gilded={omen.tier === 2}
                    tag={omen.tier === 2 ? t('Greater omen') : null}
                    caption={(
                      <>
                        <div>{omenText(omen)}</div>
                        <div style={{ color: ACCENT_GOLD, marginTop: '0.3cqw' }}>{rewardLabel(omen.reward)}</div>
                      </>
                    )}
                    selected={omenId === omen.id}
                    dimmed={Boolean(omenId) && omenId !== omen.id}
                    onClick={() => this.pick(omen.id)}
                    testId={`omen-${omen.id}`}
                  />
                  {/* The seal sits under the kept card, so the choice and its
                      confirmation read as one gesture. */}
                  <AnimatePresence>
                    {omenId === omen.id ? (
                      <motion.div
                        key="seal"
                        className="absolute left-1/2"
                        style={{ top: '100%', marginTop: '0.875cqw' }}
                        initial={{ opacity: 0, y: '-0.5cqw', x: '-50%' }}
                        animate={{ opacity: 1, y: 0, x: '-50%' }}
                        exit={{ opacity: 0, y: '-0.375cqw', x: '-50%' }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                      >
                        <ForgedButton className="px-6 py-3 text-[1.125cqw]" disabled={busy} onClick={() => onSeal(omen.id)}>
                          {t('Seal the reading')}
                        </ForgedButton>
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Room for the seal under the kept card. */}
        <motion.div className="flex flex-col items-center" style={{ gap: '0.75cqw', marginTop: '2.5cqw' }} {...float(0.35)}>
          {error ? <div style={{ color: '#e89090', fontSize: '0.8125cqw' }}>{error}</div> : null}
        </motion.div>

        {/* Foresight stays centred at the foot; the re-roll hangs off its
            right, out of the flow, so the gauge never shifts. */}
        {reading ? (
          <motion.div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: '3cqw' }} {...float(0.45)}>
            <ForesightGauge value={reading.foresight} max={reading.maxForesight} />
            {rerollable ? (
              <div className="absolute top-1/2 -translate-y-1/2" style={{ left: '100%', marginLeft: '1.5cqw' }}>
                <ForgedButton className="px-5 py-2 text-[0.9375cqw]" disabled={!canReroll || busy} onClick={this.reroll}>
                  {t('New omens ({n} Foresight)', { n: rerollCost })}
                </ForgedButton>
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </motion.div>
    );
  }
}
